import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {MilestoneEdit} from '@github-ui/repository-milestone/MilestoneEdit'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {useQueryContext} from '../../contexts/QueryContext'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {RepositoryMilestoneEditPageContentInternal$key} from './__generated__/RepositoryMilestoneEditPageContentInternal.graphql'
import type {RepositoryMilestoneEditPageQuery} from './__generated__/RepositoryMilestoneEditPageQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneEditPageQuery($name: String!, $owner: String!, $number: Int!) {
    repository(owner: $owner, name: $name) {
      ...RepositoryMilestoneEditPageContentInternal @arguments(number: $number)
    }
  }
`

export const RepositoryMilestoneEditPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneEditPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Edit">
      <RepositoryMilestoneEditPageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneEditPageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneEditPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneEditPageQuery>(PageQuery, pageQueryRef)

  const {setCurrentViewId} = useQueryContext()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  if (!pageData.repository) {
    reportError(
      new Error(`Could not find repository when loading milestone edit page for ${ssrSafeLocation?.href.toString()}`),
    )
    return <div>Repository not found</div>
  }
  return <RepositoryMilestoneEditPageContentInternal repository={pageData.repository} />
}

function RepositoryMilestoneEditPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneEditPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneEditPageContentInternal on Repository @argumentDefinitions(number: {type: "Int!"}) {
        ...MilestoneEditFormRepositoryQuery @arguments(number: $number)
      }
    `,
    repository,
  )

  return <MilestoneEdit repository={data} />
}

try{ RepositoryMilestoneEditPageContent.displayName ||= 'RepositoryMilestoneEditPageContent' } catch {}
try{ RepositoryMilestoneEditPageContentInternal.displayName ||= 'RepositoryMilestoneEditPageContentInternal' } catch {}